
import React from 'react'
import PropTypes from 'prop-types'
import { css } from '@emotion/core'
import { ThemeProvider } from '@emotion/react'
import IntlProvider from '@/hooks/intl/IntlProvider'
import { injectWorldReady } from '@paypalcorp/worldready-react'
import { PAYPAL_THEME } from '@paypalcorp/pp-react-theme'
import { screenSmall } from 'components/utils/screen-sizes'
import FullPageSpinner from '../utils/FullPageSpinner'
import ModalNavBar from '../modal/ModalNavBar'
import {
  selectShowMainPageSpinner,
  selectMainPageRetryFetchName,
  selectMainPageUnexpectedClientError,
} from '@/features/ui/ui-slice'
import { useSelector } from 'react-redux'
import { getErrorHandlerData } from 'utils/error/error-handler'
import { serverData } from 'utils/server-data'
import { isPayrollPartnerVariantOrIntent } from 'utils/flow'
import { StepperType } from 'components/stepper'
import '@paypalcorp/pp-vx/dist/components/modal/modal.css'

// Custom CSS that mimics modal content styling within main page layout
const enrollmentContentCss = css`
  max-width: 44.875rem;
  margin: 0 auto;
  padding: 0 1rem;
  min-height: calc(100vh - 286px); // Account for header + footer
  
  // Apply modal-like content styling
  .ppvx_modal__contents {
    padding: 0 2rem 4rem;
  }

  @media (max-width: ${screenSmall}) {
    margin: 0 auto;
    padding: 0 1rem;
    min-height: calc(100vh - 400px);
    
    .ppvx_modal__contents {
      padding: 0 1rem 4rem;
    }
  }
`

// CSS for navigation bar styling - completely remove modal backgrounds
const navBarCss = css`
  background: transparent !important;
  
  .ppvx_modal-header,
  .ppvx_modal-header.ppvx_modal-header--no-logo {
    background: transparent !important;
    background-color: transparent !important;
    margin-bottom: 0.5rem;
    padding: 0.5rem 0 0.25rem 0;
  }
  
  .ppvx_modal-header__title {
    background: transparent !important;
    background-color: transparent !important;
  }
  
  .ppvx_modal-header__action {
    background: transparent !important;
    background-color: transparent !important;
  }
  
  .ppvx_modal-header div,
  .ppvx_modal-header header,
  .ppvx_modal-header * {
    background: transparent !important;
    background-color: transparent !important;
  }
`

// Logic copied from FlowModal for back button visibility
const isShowBackButton = ({
  stepper,
  retryFetchName,
  terminalSteps,
  nonTerminalSteps,
  loadingSteps,
  onFirstStepBack,
  showModalPageSpinner,
}) => {
  if (!stepper || showModalPageSpinner || retryFetchName) {
    return false
  }
  
  const hideBackButtonOnTerminalStep = terminalSteps.includes(stepper.getCurrentStepName())
  const showBackButtonInBetweenFirstAndLastStep = !stepper.isFirstStep() && !stepper.isLastStep()
  const showBackButtonInNonTerminalStep = nonTerminalSteps.includes(stepper.getCurrentStepName())
  const showBackButtonInFirstStep = onFirstStepBack && stepper.isFirstStep()
  const hideBackButtonOnLoadingStep = loadingSteps.includes(stepper.getCurrentStepName())

  return (
    !showModalPageSpinner &&
    !retryFetchName &&
    !hideBackButtonOnTerminalStep &&
    !hideBackButtonOnLoadingStep &&
    (showBackButtonInFirstStep ||
      showBackButtonInBetweenFirstAndLastStep ||
      showBackButtonInNonTerminalStep)
  )
}

/**
 * Hybrid layout for GB enrollment flows that provides:
 * - Main page layout (includes footer)
 * - Modal-like content styling for proper appearance
 * - Navigation bar with back and close buttons
 */
function EnrollmentMainPageLayout({ 
  flowName, 
  worldReady, 
  children,
  stepper,
  onClose,
  onFirstStepBack,
  terminalSteps = [],
  nonTerminalSteps = [],
  loadingSteps = [],
}) {
  // v2 UI state
  const mainPageUnexpectedClientError = useSelector(selectMainPageUnexpectedClientError)
  const mainPageRetryFetchName = useSelector(selectMainPageRetryFetchName)
  const showMainPageSpinner = useSelector(selectShowMainPageSpinner)

  const { fetchRetried, retryHandler } = useSelector(
    (state: any) => state.ui.fetches[mainPageRetryFetchName] || {},
  )

  const { errorContent } = getErrorHandlerData({
    flowName,
    fetchRetried,
    retryHandler,
    retryFetchName: mainPageRetryFetchName,
    unexpectedClientError: mainPageUnexpectedClientError,
    isSessionExpired: undefined,
    loginUrl: undefined,
    isRequestBlocked: undefined,
  })

  // Provide default stepper if none exists (similar to FlowModal)
  let currentStepper = stepper
  if (!currentStepper) {
    currentStepper = {
      isFirstStep: () => true,
      isLastStep: () => true,
      getCurrentStepName: () => '',
      previousStep: () => {},
    }
  }

  // Navigation bar logic (copied from FlowModal)
  const showBackButton = isShowBackButton({
    stepper: currentStepper,
    retryFetchName: mainPageRetryFetchName,
    terminalSteps,
    nonTerminalSteps,
    loadingSteps,
    onFirstStepBack,
    showModalPageSpinner: showMainPageSpinner,
  })

  const hideCloseButtonBasedOnIntent = isPayrollPartnerVariantOrIntent()
  const showTitle = !serverData.isMobileNative
  const hideCloseButton =
    serverData.isMobileNative ||
    loadingSteps.includes(currentStepper.getCurrentStepName()) ||
    hideCloseButtonBasedOnIntent
  const hideNavBar = !showBackButton && hideCloseButton && !hideCloseButtonBasedOnIntent

  const content = errorContent || children

  return (
    <IntlProvider worldReady={worldReady}>
      <ThemeProvider theme={PAYPAL_THEME}>
        {showMainPageSpinner && <FullPageSpinner mainPage />}
        <div css={enrollmentContentCss}>
          {!hideNavBar && (
            <div css={navBarCss} style={{ position: 'relative' }}>
              {/* <PayPalLogo overlay height="30px" /> */}
              <ModalNavBar
                // titleContent={modalTitleContent}
                stepper={currentStepper}
                onClose={onClose}
                onFirstStepBack={onFirstStepBack}
                hideCloseButton={hideCloseButton}
                showBackButton={showBackButton}
                showTitle={showTitle}
              />
            </div>
          )}
          <div className="ppvx_modal__contents">
            {content}
          </div>
        </div>
      </ThemeProvider>
    </IntlProvider>
  )
}

EnrollmentMainPageLayout.propTypes = {
  flowName: PropTypes.string,
  worldReady: PropTypes.object,
  children: PropTypes.any,
  stepper: StepperType,
  onClose: PropTypes.func,
  onFirstStepBack: PropTypes.func,
  terminalSteps: PropTypes.array,
  nonTerminalSteps: PropTypes.array,
  loadingSteps: PropTypes.array,
}

export default injectWorldReady(EnrollmentMainPageLayout) 